import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getPlans, createCheckout, type Plan } from '../../api/client';

const CHECK_ICON = (
  <svg className="h-5 w-5 shrink-0 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

export default function Pricing() {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [plans, setPlans]       = useState<Plan[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [upgrading, setUpgrading] = useState<string | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const wasCancelled = urlParams.get('cancelled') === '1';

  useEffect(() => {
    getPlans()
      .then((data) => setPlans(data))
      .catch(() => setError('Could not load plans. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleUpgrade = async (plan: Plan, provider: 'stripe' | 'paystack') => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setUpgrading(plan.slug);
    setError('');
    try {
      const { url } = await createCheckout(plan.slug, provider);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout. Please try again.');
    } finally {
      setUpgrading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#1c1917_0%,#3f2a15_60%,#1c1917_100%)] pt-14 text-white">
      {/* Header */}
      <div className="px-4 pt-8 pb-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-stone-400 hover:text-amber-300 text-sm transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
          Back
        </button>
      </div>

      <div className="mx-auto max-w-4xl px-4 pb-24">
        {/* Hero */}
        <div className="py-10 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-200 to-orange-400 mb-5">
            <img src="/logo.png" alt="Frequency Locus" className="h-9 w-9 object-contain" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Simple, honest pricing</h1>
          <p className="mt-3 text-stone-400 text-base md:text-lg max-w-md mx-auto">
            One upgrade. Full access to everything Frequency Locus has to offer.
          </p>
          {wasCancelled && (
            <p className="mt-4 text-amber-300/80 text-sm">Checkout was cancelled — no charge was made.</p>
          )}
          {error && (
            <p className="mt-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2 inline-block">{error}</p>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            {[0, 1].map((i) => (
              <div key={i} className="h-80 rounded-3xl border border-stone-700 bg-stone-900/50 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {plans.map((plan) => {
              const isPro = plan.slug !== 'free';
              const features: string[] = Array.isArray(plan.features)
                ? plan.features
                : JSON.parse(plan.features as unknown as string);

              return (
                <div
                  key={plan.slug}
                  className={`relative rounded-3xl border p-7 flex flex-col gap-6 ${
                    isPro
                      ? 'border-amber-500/50 bg-gradient-to-b from-amber-950/60 to-stone-950/80 shadow-[0_0_40px_rgba(245,158,11,0.12)]'
                      : 'border-stone-700/60 bg-stone-900/40'
                  }`}
                >
                  {isPro && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-amber-400 to-orange-400 text-stone-950 text-xs font-bold tracking-widest uppercase px-4 py-1 rounded-full">
                        Most popular
                      </span>
                    </div>
                  )}

                  {/* Plan header */}
                  <div>
                    <h2 className="text-xl font-bold">{plan.name}</h2>
                    <p className="mt-1 text-stone-400 text-sm">{plan.description}</p>
                    <div className="mt-4 flex items-end gap-1">
                      {plan.price_usd === 0 ? (
                        <span className="text-4xl font-extrabold text-white">Free</span>
                      ) : (
                        <>
                          <span className="text-4xl font-extrabold text-white">${plan.price_usd}</span>
                          <span className="text-stone-400 text-sm mb-1.5">/month</span>
                        </>
                      )}
                    </div>
                    {isPro && plan.price_ngn > 0 && (
                      <p className="mt-1 text-stone-500 text-xs">
                        or ₦{Number(plan.price_ngn).toLocaleString()}/month via Paystack
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-2.5 flex-1">
                    {features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5 text-sm text-stone-300">
                        {CHECK_ICON}
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {isPro ? (
                    <div className="space-y-2.5">
                      <button
                        type="button"
                        disabled={upgrading === plan.slug}
                        onClick={() => handleUpgrade(plan, 'stripe')}
                        className="w-full bg-gradient-to-r from-amber-400 to-orange-400 text-stone-950 font-semibold py-3 rounded-xl text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                      >
                        {upgrading === plan.slug ? 'Redirecting…' : 'Upgrade with card (USD)'}
                      </button>
                      <button
                        type="button"
                        disabled={upgrading === plan.slug}
                        onClick={() => handleUpgrade(plan, 'paystack')}
                        className="w-full bg-white/8 border border-stone-600 text-white font-medium py-3 rounded-xl text-sm hover:bg-white/12 transition-colors disabled:opacity-50"
                      >
                        {upgrading === plan.slug ? 'Redirecting…' : 'Pay with Paystack (₦)'}
                      </button>
                      <p className="text-center text-stone-500 text-xs">Cancel any time · No hidden fees</p>
                    </div>
                  ) : (
                    <div>
                      <div className="w-full border border-stone-700 text-stone-400 font-medium py-3 rounded-xl text-sm text-center">
                        Your current plan
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ teaser */}
        <div className="mt-14 text-center">
          <p className="text-stone-500 text-sm">
            Questions?{' '}
            <a href="mailto:hello@frequencylocus.com" className="text-amber-400 hover:text-amber-300 transition-colors">
              hello@frequencylocus.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
